import React, { useState, useEffect } from 'react';
import { getPlans, getCurrentSubscription, subscribe, upgrade, cancelSubscription } from '../api/subscriptions';
import { HiOutlineCheck } from 'react-icons/hi2';
import toast from 'react-hot-toast';

export default function Subscription() {
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null);
  const [cycle, setCycle] = useState('monthly');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getPlans(), getCurrentSubscription().catch(() => null)])
      .then(([plansRes, subRes]) => {
        setPlans(plansRes.data.data.plans);
        if (subRes) setCurrent(subRes.data.data.subscription);
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSubscribe = async (planId) => {
    try {
      const action = current?.status === 'active' ? upgrade : subscribe;
      const res = await action({ plan_id: planId, billing_cycle: cycle });
      setCurrent(res.data.data.subscription);
      toast.success(current?.status === 'active' ? 'Plan upgraded!' : 'Subscribed!');
    } catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  const handleCancel = async () => {
    if (!window.confirm('Cancel your subscription? You will lose access at the end of the billing period.')) return;
    try { const res = await cancelSubscription(); setCurrent(res.data.data.subscription); toast.success('Subscription cancelled'); }
    catch (err) { toast.error(err.response?.data?.message || 'Failed'); }
  };

  if (loading) return <div className="text-center py-12 text-dark-400">Loading...</div>;

  return (
    <div>
      <h1 className="text-xl font-bold text-dark-800 mb-2">Billing & Subscription</h1>

      {current && (
        <div className="bg-white rounded-xl border border-dark-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <p className="text-sm text-dark-400">Current Plan</p>
              <p className="text-lg font-bold text-dark-800">{current.plan_name} <span className="text-sm font-normal text-dark-400">({current.billing_cycle})</span></p>
              <p className="text-sm text-dark-500">PKR {Number(current.amount).toLocaleString()} / {current.billing_cycle === 'yearly' ? 'year' : 'month'}</p>
              <p className="text-xs text-dark-400 mt-1">
                {current.status === 'trial' && `Trial ends ${new Date(current.end_at).toLocaleDateString()}`}
                {current.status === 'active' && `Renews ${new Date(current.end_at).toLocaleDateString()}`}
                {current.status === 'cancelled' && 'Cancelled'}
              </p>
            </div>
            {(current.status === 'active' || current.status === 'trial') && (
              <button onClick={handleCancel} className="px-4 py-2 border border-red-300 text-red-500 rounded-lg text-sm font-medium hover:bg-red-50">
                Cancel Subscription
              </button>
            )}
          </div>
        </div>
      )}

      <div className="flex items-center justify-center gap-3 mb-6">
        <span className={`text-sm ${cycle === 'monthly' ? 'font-bold text-dark-800' : 'text-dark-400'}`}>Monthly</span>
        <button onClick={() => setCycle(cycle === 'monthly' ? 'yearly' : 'monthly')}
          className={`w-12 h-6 rounded-full transition-colors relative ${cycle === 'yearly' ? 'bg-primary-500' : 'bg-dark-300'}`}>
          <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${cycle === 'yearly' ? 'translate-x-6' : 'translate-x-0.5'}`} />
        </button>
        <span className={`text-sm ${cycle === 'yearly' ? 'font-bold text-dark-800' : 'text-dark-400'}`}>Yearly <span className="text-primary-500 text-xs">Save 17%</span></span>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {plans.map((plan) => {
          const price = cycle === 'yearly' ? plan.yearly_price : plan.monthly_price;
          const isCurrent = current?.plan_id === plan.id && current?.billing_cycle === cycle;
          return (
            <div key={plan.id} className={`bg-white rounded-xl border-2 p-5 ${isCurrent ? 'border-primary-500' : 'border-dark-200'}`}>
              <h3 className="font-bold text-lg text-dark-800">{plan.plan_name}</h3>
              <p className="text-2xl font-bold text-dark-800 mt-2">
                PKR {Number(price).toLocaleString()}
                <span className="text-sm font-normal text-dark-400"> / {cycle === 'yearly' ? 'year' : 'month'}</span>
              </p>
              <ul className="mt-4 space-y-2 text-sm text-dark-600">
                <li className="flex items-center gap-2"><HiOutlineCheck className="w-4 h-4 text-primary-500" /> {plan.max_users} users</li>
                <li className="flex items-center gap-2"><HiOutlineCheck className="w-4 h-4 text-primary-500" /> {plan.max_contacts.toLocaleString()} contacts</li>
                <li className="flex items-center gap-2"><HiOutlineCheck className="w-4 h-4 text-primary-500" /> {plan.max_daily_messages.toLocaleString()} daily messages</li>
                <li className="flex items-center gap-2"><HiOutlineCheck className="w-4 h-4 text-primary-500" /> {plan.max_whatsapp_sessions} WA session{plan.max_whatsapp_sessions > 1 ? 's' : ''}</li>
                <li className="flex items-center gap-2"><HiOutlineCheck className="w-4 h-4 text-primary-500" /> {plan.storage_limit_mb >= 1000 ? `${plan.storage_limit_mb / 1000} GB` : `${plan.storage_limit_mb} MB`} storage</li>
              </ul>
              <button
                onClick={() => handleSubscribe(plan.id)}
                disabled={isCurrent}
                className={`w-full mt-5 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isCurrent ? 'bg-gray-100 text-dark-400 cursor-default' : 'bg-primary-500 text-white hover:bg-primary-600'
                }`}
              >
                {isCurrent ? 'Current Plan' : current?.status === 'active' ? 'Upgrade' : 'Subscribe'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
